import React, { useState, useEffect } from 'react';
import { Phone, MessageCircle, FileText } from 'lucide-react';
import TextLink from './TextLink';

const MoldStickyCTA = ({ onEstimateClick }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Show after scrolling 300px
      const scrolled = window.scrollY > 300;
      setHasScrolled(scrolled);
      
      // Check if we're near the footer (hide if so)
      const footer = document.querySelector('footer');
      if (footer) {
        const footerRect = footer.getBoundingClientRect();
        const isNearFooter = footerRect.top < window.innerHeight + 100;
        setIsVisible(scrolled && !isNearFooter);
      } else {
        setIsVisible(scrolled);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Only show on mobile/tablet
  if (typeof window !== 'undefined' && window.innerWidth > 1024) {
    return null;
  }

  const handleCallClick = () => {
    // Fire tracking event (no PII)
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'mold_call_click',
        cta_location: 'sticky_mobile_bar',
        service_name: 'mold-remediation'
      });
    }
  };

  const handleTextClick = () => {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'mold_text_click',
        cta_location: 'sticky_mobile_bar',
        service_name: 'mold-remediation'
      });
    }
  };

  const handleEstimateClick = () => {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'mold_form_start',
        cta_location: 'sticky_mobile_bar',
        service_name: 'mold-remediation'
      });
    }
    if (onEstimateClick) {
      onEstimateClick();
    } else {
      // Scroll to form
      const form = document.getElementById('mold-lead-form');
      if (form) {
        form.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  return (
    <div 
      className={`lg:hidden fixed bottom-0 left-0 right-0 z-40 transform transition-transform duration-300 ${
        isVisible ? 'translate-y-0' : 'translate-y-full'
      }`}
      role="navigation"
      aria-label="Quick contact actions"
    >
      {/* Safe area spacer for iOS */}
      <div className="bg-neutral-900 border-t border-neutral-700">
        <div className="flex items-stretch">
          {/* Call Button */}
          <a 
            href="tel:+12149087644"
            onClick={handleCallClick}
            className="flex-1 flex flex-col items-center justify-center py-3 px-2 text-white hover:bg-neutral-800 active:bg-neutral-700 transition-colors border-r border-neutral-700"
            aria-label="Call Gray Contracting at 214-908-7644"
          >
            <Phone className="w-5 h-5 mb-1" aria-hidden="true" />
            <span className="text-xs font-medium">Call</span>
          </a>

          {/* Text Button */}
          <TextLink 
            className="flex-1 flex flex-col items-center justify-center py-3 px-2 text-white hover:bg-neutral-800 active:bg-neutral-700 transition-colors border-r border-neutral-700"
            onClick={handleTextClick}
            aria-label="Text photos to Gray Contracting"
          >
            <MessageCircle className="w-5 h-5 mb-1" aria-hidden="true" />
            <span className="text-xs font-medium">Text Photos</span>
          </TextLink>

          {/* Get Estimate Button */}
          <button
            onClick={handleEstimateClick}
            className="flex-1 flex flex-col items-center justify-center py-3 px-2 bg-amber-500 text-white hover:bg-amber-600 active:bg-amber-700 transition-colors"
            aria-label="Request a mold remediation estimate"
          >
            <FileText className="w-5 h-5 mb-1" aria-hidden="true" />
            <span className="text-xs font-medium">Get Estimate</span>
          </button>
        </div>
        
        {/* iOS safe area */}
        <div className="h-safe-area-inset-bottom bg-neutral-900" />
      </div>
    </div>
  );
};

export default MoldStickyCTA;
